import { useState, useEffect } from 'react'
import { Building2, X, FileText, Scroll, Check, AlertCircle, Eye, EyeOff, Loader2, Mail } from 'lucide-react'
import { COUNTRIES, getDefaultCurrency } from '../lib/localization'
import { supabase } from '../lib/supabase'
import { useToast } from '../lib/ToastContext'
import { getCountryIdTypes } from '../lib/idTypes'
import { checkRateLimit } from '../lib/rateLimit'
import AuthHeader from '../components/AuthHeader'

function GoogleIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  )
}

const COOKIE_KEY = 'bizosCookieConsent'

const BLOCKED_EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'yahoo.co.uk', 'icloud.com', 'live.com', 'aol.com']

const TERMS_TEXT = `Terms of Service\n\n1. Acceptance of Terms\nBy accessing and using Bizos Suite ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree, please discontinue use immediately.\n\n2. Platform Description\nBizos Suite provides enterprise finance governance, compliance document management, and investor portal infrastructure for registered companies. The Platform is not a financial advisor, legal counsel, or investment broker.\n\n3. User Responsibilities\nYou are responsible for the accuracy of all documents uploaded to the Platform. You must maintain truthful financial records and comply with all applicable local and international regulations.\n\n4. Subscription and Billing\nAccess to certain features requires a paid subscription. Subscriptions are billed monthly in your selected currency. Agent service tokens are non-refundable once purchased.\n\n5. Intellectual Property\nAll templates, blueprints, and compliance matrices provided through the Platform remain the intellectual property of Bizos Suite. You receive a limited license to use these materials within your registered company.\n\n6. Limitation of Liability\nBizos Suite shall not be liable for any indirect, incidental, or consequential damages arising from the use of the Platform. All investment decisions made through the Platform are solely at your own risk.\n\n7. Termination\nWe reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.`

const PRIVACY_TEXT = `Privacy Policy\n\n1. Data Collection\nBizos Suite collects company information, founder identity data, financial records, and compliance documents necessary for platform operation. We do not sell your data to third parties.\n\n2. Data Storage and Security\nAll data is encrypted at rest and in transit. Documents are stored in isolated tenant containers with row-level security policies. Access is logged through our audit trail system.\n\n3. Data Usage\nYour data is used to provide governance tools, generate compliance reports, and facilitate investor access upon your explicit approval. We use analytical data nodes to ensure institutional compliance integrity.\n\n4. Investor Access\nInvestors may only view your documents through time-limited access tokens that you explicitly generate and approve. You may revoke access at any time.\n\n5. Data Retention\nFinancial records are retained for the duration of your subscription. Upon cancellation, you may export your data for 30 days before it is permanently deleted.\n\n6. Cookie Usage\nWe use essential cookies to maintain session integrity and authentication. Analytical cookies help us improve platform performance and compliance monitoring.\n\n7. Your Rights\nYou may request data export, correction, or deletion at any time. Contact compliance@bizossuite.com for data-related inquiries.`

// ✅ Password strength checker
function checkPasswordStrength(password) {
  if (!password) return { isValid: false, feedback: ['Password is required'], score: 0 }
  
  let score = 0
  const feedback = []
  
  if (password.length >= 8) score++; else feedback.push('at least 8 characters')
  if (/[A-Z]/.test(password)) score++; else feedback.push('uppercase letter')
  if (/[a-z]/.test(password)) score++; else feedback.push('lowercase letter')
  if (/[0-9]/.test(password)) score++; else feedback.push('number')
  if (/[^A-Za-z0-9]/.test(password)) score++; else feedback.push('special character')
  
  return { 
    isValid: score >= 3, 
    feedback, 
    score 
  }
}

export default function Login({ onOnboarded }) {
  const [tab, setTab] = useState('signin')
  const [legalModal, setLegalModal] = useState(null)
  const [cookieAccepted, setCookieAccepted] = useState(false)
  const [authError, setAuthError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [signUpSuccess, setSignUpSuccess] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    try {
      setCookieAccepted(localStorage.getItem(COOKIE_KEY) === 'true')
    } catch {
      setCookieAccepted(true)
    }
  }, [])

  const acceptCookies = () => {
    try { localStorage.setItem(COOKIE_KEY, 'true') } catch {}
    setCookieAccepted(true)
  }

  // sign-in fields
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showSignInPassword, setShowSignInPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [forgotMode, setForgotMode] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSent, setForgotSent] = useState(false)
  const [forgotLoading, setForgotLoading] = useState(false)

  // create-account fields
  const [company, setCompany] = useState('')
  const [founderEmail, setFounderEmail] = useState('')
  const [regPassword, setRegPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [country, setCountry] = useState('Nigeria')
  const [showCountryModal, setShowCountryModal] = useState(false)
  const [googleCountry, setGoogleCountry] = useState('Nigeria')

  const passwordStrength = (() => {
    const p = regPassword
    if (!p) return { score: 0, label: '', color: '', suggestions: [] }
    let score = 0
    const suggestions = []
    if (p.length >= 8) score++; else suggestions.push('Use at least 8 characters')
    if (/[A-Z]/.test(p)) score++; else suggestions.push('Include uppercase letters')
    if (/[a-z]/.test(p)) score++; else suggestions.push('Include lowercase letters')
    if (/[0-9]/.test(p)) score++; else suggestions.push('Include numbers')
    if (/[^A-Za-z0-9]/.test(p)) score++; else suggestions.push('Include special characters')
    const labels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong']
    const colors = ['bg-rose-500', 'bg-rose-400', 'bg-amber-400', 'bg-emerald-400', 'bg-emerald-500']
    const idx = Math.min(score, 4)
    return { score, label: labels[idx], color: colors[idx], suggestions }
  })()

  // ✅ Google Sign-in with better error handling
  const signInWithGoogle = async () => {
    setAuthError(null)
    try {
      const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin
      const redirectUrl = `${baseUrl}/auth/v1/callback`
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      })
      if (error) throw error
    } catch (err) {
      const message = err?.message?.includes('Unsupported provider')
        ? 'Google sign-in is not enabled yet. Please use email sign-in.'
        : (err.message || 'Google sign-in failed')
      setAuthError(message)
      toast('Google sign-in failed: ' + message, 'error')
    }
  }
  
  // After Google OAuth redirect, check if user needs country selection
  useEffect(() => {
    const checkGoogleUser = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) return
      const { data: profile } = await supabase.from('profiles').select('id, company_id, onboarding_complete').eq('id', session.user.id).maybeSingle()
      if (profile && !profile.onboarding_complete && !profile.company_id) {
        setShowCountryModal(true)
      }
    }
    checkGoogleUser()
  }, [])

  const saveGoogleCountry = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user) return
    const defaultCurrency = getDefaultCurrency(googleCountry).code
    const { data: newCompany } = await supabase
      .from('companies')
      .insert({ name: session.user.email.split('@')[0] + ' Business', slug: session.user.email.split('@')[0].toLowerCase().replace(/[^a-z0-9]+/g, '-'), country: googleCountry, currency: defaultCurrency, subscription_tier: 'seed', owner_id: session.user.id })
      .select().single()
    if (newCompany) {
      await supabase.from('profiles').upsert({ id: session.user.id, email: session.user.email, role: 'founder', company_id: newCompany.id, onboarding_complete: false })
      await supabase.from('subscriptions').insert({ company_id: newCompany.id, plan: 'seed', status: 'active', renewal_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0] })
    }
    setShowCountryModal(false)
    toast('Country saved. Complete your profile to finish onboarding.', 'success')
    onOnboarded?.({ founderEmail: session.user.email, country: googleCountry, currency: defaultCurrency, role: 'founder', companyId: newCompany?.id })
  }

  // ✅ Sign In
  const signIn = async (e) => {
    e.preventDefault()
    setAuthError(null)
    
    if (!email || !email.trim()) {
      setAuthError('Email is required.')
      return
    }
    if (!password || !password.trim()) {
      setAuthError('Password is required.')
      return
    }
    
    const rl = checkRateLimit('login', email || 'anon')
    if (!rl.allowed) {
      setAuthError('Too many login attempts. Please wait a minute and try again.')
      return
    }
    
    setSubmitting(true)
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: email.trim(), 
        password: password 
      })
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setAuthError('Invalid email or password. Please try again.')
          toast('Invalid credentials', 'error')
          return
        }
        throw error
      }
      
      toast('Signed in successfully.', 'success')
      const { data: profile } = await supabase.from('profiles').select('role, company_id, onboarding_complete').eq('id', data.user.id).maybeSingle()
      const role = profile?.role || 'founder'

      // ✅ Navigate directly to dashboard based on role
      let targetView = 'admin' // default for founder
      if (role === 'admin') targetView = 'platform'
      else if (role === 'staff') targetView = 'staff'
      else if (role === 'agent') targetView = 'agent-projects'
      
      onOnboarded?.({ 
        email: email.trim(), 
        role, 
        companyId: profile?.company_id, 
        onboardingComplete: profile?.onboarding_complete,
        targetView 
      })
    } catch (err) {
      setAuthError(err.message || 'Sign in failed. Please try again.')
      toast('Sign-in failed: ' + (err.message || 'Unknown error'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // ✅ Create Account - FIXED
  const createAccount = async (e) => {
    e.preventDefault()
    setAuthError(null)
    
    // Validation
    if (!company || !company.trim()) {
      setAuthError('Company name is required.')
      return
    }
    
    if (!founderEmail || !founderEmail.trim()) {
      setAuthError('Founder email is required.')
      return
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(founderEmail.trim())) {
      setAuthError('Please enter a valid email address.')
      return
    }
    
    if (!regPassword || regPassword.length < 8) {
      setAuthError('Password must be at least 8 characters.')
      return
    }
    
    const rl = checkRateLimit('signup', founderEmail || 'anon')
    if (!rl.allowed) {
      setAuthError('Too many attempts. Please wait a minute and try again.')
      return
    }
    
    const emailDomain = founderEmail.split('@')[1]?.toLowerCase()
    if (BLOCKED_EMAIL_DOMAINS.includes(emailDomain)) {
      setAuthError('Bizos requires a verified corporate domain email')
      toast('Bizos requires a verified corporate domain email', 'error')
      return
    }
    
    setSubmitting(true)
    try {
      const { data, error } = await supabase.auth.signUp({ 
        email: founderEmail.trim(), 
        password: regPassword,
        options: {
          data: {
            full_name: company.trim(),
            company_name: company.trim(),
          },
          emailRedirectTo: `${window.location.origin}/auth/verify`,
        }
      })
      
      if (error) {
        if (error.message.includes('already registered')) {
          setAuthError('This email is already registered. Please sign in instead.')
          toast('Email already exists. Please sign in.', 'error')
        } else {
          setAuthError(error.message)
          toast('Sign-up failed: ' + error.message, 'error')
        }
        setSubmitting(false)
        return
      }
      
      // ✅ SUCCESS - Show modal
      if (data?.user) {
        setSignUpSuccess(true) // ✅ THIS WAS MISSING - shows the modal
        setSubmitting(false)
        toast('Account created. Please check your email for confirmation.', 'success', 6000)
      } else {
        setAuthError('Failed to create account. Please try again.')
        setSubmitting(false)
      }
      
    } catch (err) {
      setAuthError(err.message || 'Failed to create account')
      toast('Sign-up failed: ' + (err.message || 'Unknown error'), 'error')
      setSubmitting(false)
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setAuthError(null)
    if (!forgotEmail.trim()) return
    setForgotLoading(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: `${window.location.origin}/auth/recovery`,
      })
      if (error) throw error
      setForgotSent(true)
      toast('Password reset link sent to your email.', 'success')
    } catch (err) {
      setAuthError(err.message || 'Could not send reset email')
      toast('Reset failed: ' + (err.message || 'Unknown error'), 'error')
    } finally {
      setForgotLoading(false)
    }
  }

  return (
    <div className="min-h-full">
      {/* ✅ AuthHeader - fixed at top */}
      <AuthHeader showBack={false} />

      {/* Main Content - with padding for header */}
      <div className="flex items-center justify-center bg-neutral-50 px-4 py-12 pt-28">
        <div className="w-full max-w-[440px]">
          <div className="flex flex-col items-center mb-10">
            <div className="w-14 h-14 bg-brand flex items-center justify-center mb-4">
              <Building2 className="text-white" size={26} />
            </div>
            <h1 className="text-[26px] font-bold text-ink">Bizos Suite</h1>
            <p className="text-sm text-muted mt-1">Enterprise Finance & Governance</p>
          </div>

          <div className="bg-surface border border-neutral-200">
            {/* Tabs */}
            <div className="flex border-b border-neutral-200">
              <button
                onClick={() => {
                  setTab('signin')
                  setSignUpSuccess(false) // Reset success state when switching
                }}
                className={`tab-btn relative ${
                  tab === 'signin' ? 'tab-btn-active' : 'tab-btn-inactive'
                }`}
              >
                Sign In
                {tab === 'signin' && (
                  <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-brand" />
                )}
              </button>
              <button
                onClick={() => {
                  setTab('signup')
                  setSignUpSuccess(false) // Reset success state when switching
                }}
                className={`tab-btn relative ${
                  tab === 'signup' ? 'tab-btn-active' : 'tab-btn-inactive'
                }`}
              >
                Create Account
                {tab === 'signup' && (
                  <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-brand" />
                )}
              </button>
            </div>

            <div className="p-7">
              {tab === 'signin' ? (
                <form onSubmit={signIn} className="space-y-5">
                  <h2 className="text-base font-semibold text-ink">Welcome back</h2>
                  <div>
                    <label htmlFor="email" className="label-above">Email</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="input-flat"
                      autoComplete="email"
                      name="email"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="label-above">Password</label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showSignInPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="input-flat pr-10"
                        autoComplete="current-password"
                        name="password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSignInPassword((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-ink"
                        aria-label={showSignInPassword ? 'Hide password' : 'Show password'}
                      >
                        {showSignInPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 accent-brand"
                      />
                      Remember me
                    </label>
                    <button
                      type="button"
                      onClick={() => { setForgotMode(true); setAuthError(null) }}
                      className="link-secondary"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <button type="submit" className="btn-primary w-full" disabled={submitting}>
                    {submitting ? 'Signing in...' : 'Sign In'}
                  </button>
                  {authError && (
                    <div className="flex items-center gap-2 text-xs text-rose-600 border border-rose-200 bg-rose-50 px-3 py-2">
                      <AlertCircle size={14} /> {authError}
                    </div>
                  )}
                  <div className="relative py-1">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200" /></div>
                    <div className="relative flex justify-center"><span className="bg-surface px-3 text-[11px] text-muted uppercase tracking-wider">or</span></div>
                  </div>
                  <button type="button" onClick={signInWithGoogle} className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-ink border border-neutral-200 hover:border-neutral-300 bg-surface transition-colors">
                    <GoogleIcon size={17} /> Continue with Google
                  </button>
                </form>
              ) : (
                <>
                  <form onSubmit={createAccount} className="space-y-5">
                    <h2 className="text-base font-semibold text-ink">Onboard your company</h2>
                    <div>
                      <label htmlFor="company" className="label-above">Company Name</label>
                      <input
                        id="company"
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Acme Innovations Ltd"
                        className="input-flat"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="founderEmail" className="label-above">Founder Email</label>
                      <input
                        id="founderEmail"
                        type="email"
                        value={founderEmail}
                        onChange={(e) => setFounderEmail(e.target.value)}
                        placeholder="founder@company.com"
                        className="input-flat"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="regPassword" className="label-above">Password</label>
                      <div className="relative">
                        <input
                          id="regPassword"
                          type={showPassword ? 'text' : 'password'}
                          value={regPassword}
                          onChange={(e) => setRegPassword(e.target.value)}
                          placeholder="••••••••"
                          className="input-flat pr-10"
                          autoComplete="new-password"
                          name="password"
                          required
                        />
                        <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-ink" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {regPassword && (
                        <div className="mt-2 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-neutral-100">
                              <div className={`h-full transition-all duration-300 ${passwordStrength.color}`} style={{ width: `${(passwordStrength.score / 5) * 100}%` }} />
                            </div>
                            <span className="text-[11px] font-semibold text-neutral-500 w-20 text-right">{passwordStrength.label}</span>
                          </div>
                          {passwordStrength.suggestions.length > 0 && (
                            <ul className="space-y-0.5">
                              {passwordStrength.suggestions.map((s) => (
                                <li key={s} className="text-[11px] text-muted flex items-center gap-1.5">
                                  <span className="w-1 h-1 bg-neutral-300" /> {s}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <label htmlFor="country" className="label-above">Country</label>
                      <select
                        id="country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="input-flat"
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <button 
                      type="submit" 
                      className="btn-primary w-full" 
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin mr-2" /> Creating account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </button>
                    {authError && (
                      <div className="flex items-center gap-2 text-xs text-rose-600 border border-rose-200 bg-rose-50 px-3 py-2">
                        <AlertCircle size={14} /> {authError}
                      </div>
                    )}
                    <div className="relative py-1">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200" /></div>
                      <div className="relative flex justify-center"><span className="bg-surface px-3 text-[11px] text-muted uppercase tracking-wider">or</span></div>
                    </div>
                    <button type="button" onClick={signInWithGoogle} className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-ink border border-neutral-200 hover:border-neutral-300 bg-surface transition-colors">
                      <GoogleIcon size={17} /> Sign up with Google
                    </button>
                  </form>

                  {/* ✅ Signup Success Modal - NOW OUTSIDE THE FORM */}
                  {signUpSuccess && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-ink/40">
                      <div className="bg-white border border-neutral-200 rounded-xl p-8 max-w-md w-full text-center animate-fade-in">
                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Check size={32} className="text-emerald-600" />
                        </div>
                        <h2 className="text-xl font-bold text-ink">Check Your Email! 📧</h2>
                        <p className="text-sm text-muted mt-2">
                          We've sent a confirmation link to <strong>{founderEmail}</strong>.
                          Please verify your email to continue.
                        </p>
                        <p className="text-xs text-muted mt-2">
                          (Check your spam folder if you don't see it)
                        </p>
                        <button 
                          onClick={() => {
                            setSignUpSuccess(false)
                            setTab('signin')
                            setEmail(founderEmail)
                          }}
                          className="btn-primary w-full mt-6"
                        >
                          Back to Sign In
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <p className="text-center text-sm text-muted mt-6">
            {tab === 'signin' ? (
              <>Need an account? <button onClick={() => { setTab('signup'); setSignUpSuccess(false) }} className="link-secondary">Create one</button></>
            ) : (
              <>Already registered? <button onClick={() => { setTab('signin'); setSignUpSuccess(false) }} className="link-secondary">Sign in</button></>
            )}
          </p>

          {/* Footer links */}
          <div className="flex items-center justify-center gap-4 mt-6 pb-4">
            <button onClick={() => setLegalModal('terms')} className="text-xs text-muted hover:text-brand transition-colors flex items-center gap-1">
              <Scroll size={12} /> Terms of Service
            </button>
            <span className="text-neutral-300">|</span>
            <button onClick={() => setLegalModal('privacy')} className="text-xs text-muted hover:text-brand transition-colors flex items-center gap-1">
              <FileText size={12} /> Privacy Policy
            </button>
          </div>
        </div>
      </div>

      {/* All Modals (unchanged) */}
      {/* Google OAuth Country Selection Modal */}
      {showCountryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-ink/40" aria-hidden />
          <div className="relative bg-surface border border-neutral-200 w-full max-w-sm animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h2 className="text-sm font-semibold text-ink flex items-center gap-2"><Building2 size={15} /> Select Your Country</h2>
              <button onClick={() => setShowCountryModal(false)} className="text-neutral-400 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-xs text-muted">To complete your Google sign-up, please select your operating country so we can configure your compliance requirements.</p>
              <div>
                <label className="label-above">Country</label>
                <select value={googleCountry} onChange={(e) => setGoogleCountry(e.target.value)} className="input-flat">
                  {COUNTRIES.map((c) => (<option key={c}>{c}</option>))}
                </select>
              </div>
              <button onClick={saveGoogleCountry} className="btn-primary w-full">Continue</button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Banner */}
      {!cookieAccepted && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface border-t border-brand px-6 py-4 animate-slide-in">
          <div className="max-w-[440px] mx-auto flex items-center justify-between gap-4">
            <p className="text-xs text-muted leading-relaxed">
              We utilize essential analytical data nodes to ensure institutional compliance integrity.
            </p>
            <button onClick={acceptCookies} className="btn-primary text-xs whitespace-nowrap flex items-center gap-1.5">
              <Check size={13} /> Accept &amp; Proceed
            </button>
          </div>
        </div>
      )}

      {/* Legal Modal */}
      {legalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setLegalModal(null)} aria-hidden />
          <div className="relative bg-surface border border-neutral-200 w-full max-w-2xl max-h-[80vh] flex flex-col animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 shrink-0">
              <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                {legalModal === 'terms' ? <><Scroll size={15} /> Terms of Service</> : <><FileText size={15} /> Privacy Policy</>}
              </h2>
              <button onClick={() => setLegalModal(null)} className="text-neutral-400 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 overflow-y-auto">
              <pre className="text-xs text-neutral-600 leading-relaxed whitespace-pre-wrap font-sans">
                {legalModal === 'terms' ? TERMS_TEXT : PRIVACY_TEXT}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {forgotMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-ink/40" onClick={() => { setForgotMode(false); setForgotSent(false); setAuthError(null) }} aria-hidden />
          <div className="relative bg-surface border border-neutral-200 w-full max-w-sm animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h2 className="text-sm font-semibold text-ink flex items-center gap-2"><Mail size={15} /> Reset Password</h2>
              <button onClick={() => { setForgotMode(false); setForgotSent(false); setAuthError(null) }} className="text-neutral-400 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {forgotSent ? (
                <div className="text-center space-y-3">
                  <div className="w-10 h-10 bg-emerald-50 flex items-center justify-center mx-auto">
                    <Check size={20} className="text-emerald-600" />
                  </div>
                  <p className="text-sm font-semibold text-ink">Check your email</p>
                  <p className="text-xs text-muted">We sent a password reset link to <span className="font-semibold text-ink">{forgotEmail}</span></p>
                  <button onClick={() => { setForgotMode(false); setForgotSent(false) }} className="btn-primary w-full">Back to sign in</button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted">Enter your email and we'll send you a secure reset link.</p>
                  {authError && (
                    <div className="flex items-center gap-2 text-xs text-rose-600 border border-rose-200 bg-rose-50 px-3 py-2">
                      <AlertCircle size={14} /> {authError}
                    </div>
                  )}
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label htmlFor="forgotEmail" className="label-above">Email address</label>
                      <input
                        id="forgotEmail"
                        type="email"
                        required
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="input-flat"
                        autoComplete="email"
                        name="email"
                      />
                    </div>
                    <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={forgotLoading}>
                      {forgotLoading ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                      {forgotLoading ? 'Sending...' : 'Send reset link'}
                    </button>
                  </form>
                  <button onClick={() => { setForgotMode(false); setAuthError(null) }} className="link-secondary w-full text-center">Back to sign in</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}